import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Heart, ShoppingBag, User, LogOut, Package, MapPin, Settings } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { useCart } from '../../hooks/useCart';
import { useStoreContext } from '../../contexts/StoreContext';
import SearchBar from '../common/SearchBar';
import Drawer from '../common/Drawer';
import CategoryNavBar from './CategoryNavBar';

// Rotas onde a CategoryNavBar não aparece — detalhe do produto e fluxos de
// sacola/checkout. Foco visual nessas telas é o produto/conferência.
function shouldHideCategoryNav(pathname) {
  if (!pathname) return false;
  if (/\/produto\/[^/]+/.test(pathname)) return true;
  if (pathname.endsWith('/carrinho')) return true;
  if (pathname.endsWith('/checkout')) return true;
  return false;
}

const REVITER_FALLBACK_LOGO = '/reviter.png';

// Header dedicado ao storefront whitelabel. Linha 1: logo da loja + busca +
// ícones (conta, favoritos, carrinho). Linha 2: CategoryNavBar com cor
// primária do tema. Inspirado no mockup compartilhado.
const StorefrontHeader = () => {
  const { user, isAuthenticated, logout } = useAuth();
  const { getTotalItems } = useCart();
  const { store } = useStoreContext();
  const navigate = useNavigate();
  const location = useLocation();
  const cartCount = getTotalItems();

  const [menuOpen, setMenuOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    const onClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  // Fecha menus em qualquer mudança de rota.
  useEffect(() => {
    setMenuOpen(false);
    setDrawerOpen(false);
  }, [location.pathname]);

  const handleLogout = () => {
    setMenuOpen(false);
    setDrawerOpen(false);
    logout();
    navigate('/');
  };

  const logoSrc = store?.logoUrl || REVITER_FALLBACK_LOGO;
  const hideCategoryNav = shouldHideCategoryNav(location.pathname);

  return (
    <header className="sticky top-0 z-40 bg-white border-b border-app-border">
      <div className="w-full px-4 sm:px-6 lg:px-10 xl:px-16">
        {/* Grid 3-col [1fr_auto_1fr]: laterais com tamanho igual (logo
            justify-start, ações justify-end), busca no centro com largura
            fixa — assim ela fica visualmente centralizada na viewport,
            independente do tamanho dos blocos laterais. Em mobile vira
            flex compacto. */}
        <div className="flex md:grid md:grid-cols-[1fr_auto_1fr] items-center gap-3 py-2">
          <Link to="/" className="flex items-center gap-2 shrink-0 group md:justify-self-start">
            <img
              src={logoSrc}
              alt={store?.name || 'Reviter'}
              className="h-9 w-9 rounded-md object-contain bg-white border border-app-border group-hover:scale-105 transition-transform"
              onError={(e) => {
                if (e.currentTarget.src !== window.location.origin + REVITER_FALLBACK_LOGO) {
                  e.currentTarget.src = REVITER_FALLBACK_LOGO;
                }
              }}
            />
            <span className="hidden sm:block text-base font-bold text-app-text max-w-[180px] truncate leading-tight">
              {store?.name || 'Loja'}
            </span>
          </Link>

          <div className="flex-1 md:flex-none md:w-[min(36rem,100%)] md:justify-self-center">
            <SearchBar />
          </div>

          <div className="flex items-center gap-1 shrink-0 md:justify-self-end">
            {isAuthenticated ? (
              <>
                {/* Desktop: dropdown ancorado no botão (≥ sm) */}
                <div className="relative hidden sm:block" ref={menuRef}>
                  <button
                    type="button"
                    onClick={() => setMenuOpen((v) => !v)}
                    className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-app-surface-hover transition-colors"
                    aria-label="Minha conta"
                  >
                    <div className="h-8 w-8 rounded-full bg-primary text-white flex items-center justify-center text-sm font-bold">
                      {user.name.charAt(0).toUpperCase()}
                    </div>
                    <div className="text-left hidden md:block">
                      <span className="block text-xs text-app-text-secondary leading-none">Olá,</span>
                      <span className="block text-sm font-semibold text-app-text leading-tight max-w-[100px] truncate">
                        {user.name.split(' ')[0]}
                      </span>
                    </div>
                  </button>
                  {menuOpen && (
                    <div className="absolute right-0 mt-2 w-64 bg-white rounded-lg shadow-xl border border-app-border z-50 divide-y divide-app-border">
                      <div className="p-3">
                        <p className="font-semibold text-sm text-app-text truncate">{user.name}</p>
                        <p className="text-xs text-app-text-secondary truncate">{user.email}</p>
                      </div>
                      <nav className="p-1">
                        <Link
                          to="/minha-conta"
                          className="flex items-center gap-2 px-3 py-2 text-sm text-app-text hover:bg-primary/5 hover:text-primary rounded-md transition-colors"
                        >
                          <User size={16} /> Minha conta
                        </Link>
                        <Link
                          to="/minha-conta/pedidos"
                          className="flex items-center gap-2 px-3 py-2 text-sm text-app-text hover:bg-primary/5 hover:text-primary rounded-md transition-colors"
                        >
                          <Package size={16} /> Meus pedidos
                        </Link>
                        <Link
                          to="/minha-conta/enderecos"
                          className="flex items-center gap-2 px-3 py-2 text-sm text-app-text hover:bg-primary/5 hover:text-primary rounded-md transition-colors"
                        >
                          <MapPin size={16} /> Endereços
                        </Link>
                        {user?.role === 'store_owner' && (
                          <Link
                            to="/fornecedor/painel"
                            className="flex items-center gap-2 px-3 py-2 text-sm text-app-text hover:bg-primary/5 hover:text-primary rounded-md transition-colors"
                          >
                            <Settings size={16} /> Painel da loja
                          </Link>
                        )}
                      </nav>
                      <div className="p-1">
                        <button
                          type="button"
                          onClick={handleLogout}
                          className="w-full flex items-center gap-2 px-3 py-2 text-sm text-error hover:bg-error/5 rounded-md transition-colors"
                        >
                          <LogOut size={16} /> Sair
                        </button>
                      </div>
                    </div>
                  )}
                </div>
                {/* Mobile: drawer lateral esquerda */}
                <button
                  type="button"
                  onClick={() => setDrawerOpen(true)}
                  className="sm:hidden p-2 rounded-lg hover:bg-app-surface-hover"
                  aria-label="Minha conta"
                >
                  <User size={20} className="text-app-text" />
                </button>
              </>
            ) : (
              <>
                <Link
                  to="/entrar"
                  className="hidden sm:inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-app-text hover:text-primary transition-colors"
                >
                  <User size={18} />
                  Entrar
                </Link>
                <Link
                  to="/entrar"
                  aria-label="Entrar"
                  className="sm:hidden p-2 rounded-lg hover:bg-app-surface-hover"
                >
                  <User size={20} className="text-app-text" />
                </Link>
              </>
            )}

            <Link
              to="/minha-conta/favoritos"
              aria-label="Favoritos"
              className="p-2 rounded-lg hover:bg-app-surface-hover transition-colors"
            >
              <Heart size={20} className="text-app-text" />
            </Link>

            <Link
              to="/carrinho"
              aria-label="Sacola"
              className="relative p-2 rounded-lg hover:bg-app-surface-hover transition-colors"
            >
              <ShoppingBag size={20} className="text-app-text" />
              {cartCount > 0 && (
                <span className="absolute -top-0.5 -right-0.5 bg-primary text-white text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center ring-2 ring-white">
                  {cartCount}
                </span>
              )}
            </Link>
          </div>
        </div>
      </div>

      {!hideCategoryNav && <CategoryNavBar />}

      {/* Drawer mobile da minha-conta (lateral esquerda).
          Aparece só quando autenticado — pra guests é botão "Entrar" direto. */}
      {isAuthenticated && (
        <Drawer
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          side="left"
          size="sm"
          title="Minha conta"
          footer={
            <button
              type="button"
              onClick={handleLogout}
              className="w-full flex items-center justify-center gap-2 px-3 py-2 text-sm text-error hover:bg-error/5 rounded-md transition-colors"
            >
              <LogOut size={16} /> Sair
            </button>
          }
        >
          <div className="p-4 border-b border-app-border">
            <p className="font-semibold text-sm text-app-text truncate">{user.name}</p>
            <p className="text-xs text-app-text-secondary truncate">{user.email}</p>
          </div>
          <nav className="p-2">
            <Link
              to="/minha-conta"
              className="flex items-center gap-2 px-3 py-2 text-sm text-app-text hover:bg-primary/5 hover:text-primary rounded-md transition-colors"
            >
              <User size={16} /> Minha conta
            </Link>
            <Link
              to="/minha-conta/pedidos"
              className="flex items-center gap-2 px-3 py-2 text-sm text-app-text hover:bg-primary/5 hover:text-primary rounded-md transition-colors"
            >
              <Package size={16} /> Meus pedidos
            </Link>
            <Link
              to="/minha-conta/enderecos"
              className="flex items-center gap-2 px-3 py-2 text-sm text-app-text hover:bg-primary/5 hover:text-primary rounded-md transition-colors"
            >
              <MapPin size={16} /> Endereços
            </Link>
            {user?.role === 'store_owner' && (
              <Link
                to="/fornecedor/painel"
                className="flex items-center gap-2 px-3 py-2 text-sm text-app-text hover:bg-primary/5 hover:text-primary rounded-md transition-colors"
              >
                <Settings size={16} /> Painel da loja
              </Link>
            )}
          </nav>
        </Drawer>
      )}
    </header>
  );
};

export default StorefrontHeader;
